// DTO de `GET /api/coordinador/linea/:lineaId/detalle` (C1 — modal de detalle
// hora-a-hora de una línea desde la grilla del dashboard del coordinador).
//
// Reusa la lógica del S2 (supervisor) pero:
//   - READ-ONLY: el coordinador NO valida/invalida/edita acá (eso es del
//     supervisor). No exponemos flags `puede_invalidar`.
//   - El `turno_id` y la `fecha` vienen del query (el dashboard ya resolvió el
//     turno_actual_id), no se re-resuelven server-side por programación.
//
// Reusa `registroProduccionHoraSchema`, `lineaSchema`, `plantaSchema`.
import { z } from 'zod';
import { lineaSchema } from './linea.js';
import { plantaSchema } from './planta.js';
import { registroProduccionHoraSchema } from './registro_produccion_hora.js';
import { estadoSemaforoSchema } from './supervisor_dashboard.js';
/** Query del endpoint: turno requerido, fecha opcional (default CURRENT_DATE). */
export const coordinadorLineaDetalleQuerySchema = z.object({
    turno_id: z.string().uuid(),
    fecha: z.string().date().optional(),
});
/**
 * KPI agregado del turno para la línea. Subconjunto del KPI del S1 — solo lo
 * que el modal del coordinador necesita mostrar (read-only). null si la línea
 * no tiene KPI para ese turno/fecha (0 bloques capturados).
 */
export const coordinadorKpiLineaDetalleSchema = z.object({
    cantidad_real: z.number(),
    bloques_capturados: z.number().int(),
    meta_acumulada: z.number().nullable(),
    cumplimiento_pct: z.number().nullable(),
    estado_semaforo: estadoSemaforoSchema,
    paros_count: z.number().int(),
    paros_min_totales: z.number(),
    paro_activo: z.boolean(),
});
export const coordinadorLineaDetalleSchema = z.object({
    linea: lineaSchema.pick({ id: true, nombre: true, codigo: true }).extend({
        planta: plantaSchema.pick({ id: true, nombre: true, codigo: true }),
        area_nombre: z.string(),
    }),
    turno: z.object({
        id: z.string().uuid(),
        numero: z.number().int(),
        nombre: z.string(),
        hora_inicio: z.string(),
        hora_fin: z.string(),
    }),
    /** Líder programado para esta línea/fecha/turno. */
    programacion_lider: z.object({
        activa: z.boolean(),
        lider: z
            .object({
            id: z.string().uuid(),
            nombre: z.string(),
            apellido: z.string(),
            email: z.string().email(),
        })
            .nullable(),
        es_linea_principal: z.boolean().nullable(),
    }),
    /** KPI agregado del turno (mismo cálculo que la grilla C1). */
    kpi_linea: coordinadorKpiLineaDetalleSchema.nullable(),
    /** Bloques del turno en orden físico/operativo (incluye cruce de medianoche). */
    bloques: z.array(registroProduccionHoraSchema),
    /**
     * Diccionario SKU id → {codigo, descripcion} para los SKUs presentes en los
     * bloques. Permite a la UI mostrar el SKU sin enriquecer el registro (que
     * mantiene su shape canónico `registroProduccionHoraSchema`).
     */
    skus: z.record(z.string().uuid(), z.object({ codigo: z.string(), descripcion: z.string() })),
    /**
     * Diccionario causal_id → nombres (madre + específica) para los paros
     * presentes en los bloques. Permite a la UI mostrar el nombre del paro inline
     * sin enriquecer el `registroProduccionHoraSchema` (que conserva su shape
     * canónico con solo los ids de causal).
     */
    causales: z.record(z.string().uuid(), z.object({ madre_nombre: z.string(), especifica_nombre: z.string() })),
    hora_servidor: z.string(),
    fecha: z.string(),
});
//# sourceMappingURL=coordinador_linea_detalle.js.map